/**
 * @file redeliveryActions.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description "Refused — redeliver…" / "Edit redelivery…" as a row-menu
 *              action, the same on Search Customer Orders, Search Purchase
 *              Orders and both planning boards. The rule is
 *              utils/redelivery.redeliverAction; this adds who the user is
 *              (Edit Transport Planning or Edit sales orders; a view-only
 *              account greyed) — the server checks the same.
 *              Client only (it reads permissions), so not in the shared copy.
 */

import { hasPermission, isReadOnly } from './permissions';
import { redeliverAction, REDELIVERY_NOT_READY } from './redelivery';

/** May this user record or change a redelivery? */
export const canRedeliver = (user) => hasPermission(user, 'transport_planning') || hasPermission(user, 'orders.edit');

/**
 * What the action does for this order and user: { mode, block }.
 * @param {object} order  a customer order row (redelivery, redelivery_ready, …)
 */
export function redeliverActionFor(order, user) {
  // A search row says how many POs it has rather than whether a line is bought in.
  const hasSupplyPos = order && order.has_supply_pos !== undefined ? order.has_supply_pos
    : (order && order.po_count != null ? Number(order.po_count) > 0 : undefined);
  return redeliverAction(order && { ...order, has_supply_pos: hasSupplyPos }, {
    ready: order?.redelivery_ready !== false,
    canEdit: canRedeliver(user),
    readOnly: isReadOnly(user)
  });
}

/** The menu action object for a row; `open(orderId)` opens the Redelivery window. */
export function redeliverMenuAction(order, user, open) {
  const { mode, block } = redeliverActionFor(order, user);
  const id = order?.id;
  return {
    key: 'redeliver',
    icon: mode === 'edit' ? 'edit_calendar' : 'assignment_return',
    label: mode === 'edit' ? 'Edit redelivery…' : 'Refused — redeliver…',
    disabled: !!block || !id,
    title: block || (mode === 'edit'
      ? `Change ${order.order_number || 'the order'}'s redelivery — its date, where it is held, or cancel it`
      : `The customer refused ${order.order_number || 'it'} — record where it is held and when it goes again`),
    onClick: () => { if (!block && id) open(id); }
  };
}

export { REDELIVERY_NOT_READY };
